/**
 * useResourceList — shared logic for every "Manage <resource>" screen.
 *
 * Owns the full state matrix (loading / error / empty / delete-in-progress) so
 * each feature's list view only declares its columns + row mapping. This is what
 * keeps the dashboard consistent as features scale.
 *
 * @param {object} cfg
 * @param {() => Promise<any[]>} cfg.fetch  - returns the raw records
 * @param {(id: string, row: object) => Promise<any>} [cfg.remove] - deletes by id
 *   (the full mapped row is passed as a second arg for endpoints that need more)
 * @param {(record: any) => object} cfg.mapRow - maps a raw record to a row with `id`
 * @param {(a: object, b: object) => number} [cfg.sort] - optional comparator
 *   applied to the mapped rows after every fetch (e.g. sort by date, newest first)
 */
import { ref, onMounted } from "vue";

export function useResourceList({ fetch, remove, mapRow, sort }) {
  const rows = ref([]);
  const loading = ref(false);
  const error = ref("");

  const pendingDelete = ref(null);
  const deleting = ref(false);

  async function load() {
    loading.value = true;
    error.value = "";
    try {
      const data = await fetch();
      const mapped = (Array.isArray(data) ? data : []).map(mapRow);
      rows.value = sort ? mapped.sort(sort) : mapped;
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.message ??
        err?.response?.data ??
        err?.message ??
        "Unknown error";
      error.value = status
        ? `Error ${status}: ${msg}`
        : `Network error — ${msg}. Check that the backend is running.`;
      console.error("[useResourceList] load error:", err);
    } finally {
      loading.value = false;
    }
  }

  function askDelete(row) {
    pendingDelete.value = row;
  }

  async function confirmDelete() {
    if (!pendingDelete.value || !remove) return;
    deleting.value = true;
    try {
      await remove(pendingDelete.value.id, pendingDelete.value);
      rows.value = rows.value.filter((r) => r.id !== pendingDelete.value.id);
      pendingDelete.value = null;
    } catch {
      error.value = "Couldn't delete this item. Please try again.";
    } finally {
      deleting.value = false;
    }
  }

  onMounted(load);

  return {
    rows,
    loading,
    error,
    pendingDelete,
    deleting,
    load,
    askDelete,
    confirmDelete,
  };
}
